import { ChevronRight } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import useRequireCurrentProject from "~/modules/project/hooks/useRequireCurrentProject";

import { DrawTypeSection } from "./components/DrawTypeSection";
import { EcologicalFunctionSection } from "./components/EcologicalFunctionSection";
import { MapEditorFeatureCreateDialogTitle } from "./components/MapEditorFeatureCreateDialogTitle";
import { ObservationSection } from "./components/ObservationSection";
import OtherSection from "./components/OtherSection";
import { PlannerAreaSection } from "./components/PlannerAreaSection";
import { FeatureCreateOption } from "./components/types";
import { useDraftSelectedOption } from "./hooks/useDraftSelectedOption";
import { useFeatureCreateDialogData } from "./hooks/useFeatureCreateDialogData";

type Props = {
    onConfirm: (option: FeatureCreateOption) => void;
    initialOption: FeatureCreateOption;
    open: boolean;
    onClose: () => void;
    allowedDrawTypes?: Array<"area" | "observation" | "ecologicalFunction" | "other">;
};

export default function MapEditorFeatureCreateDialog({
    open,
    onClose,
    initialOption,
    onConfirm,
    allowedDrawTypes = ["area", "observation", "ecologicalFunction", "other"],
}: Props) {
    const { t } = useTranslation("mapEditor");
    const { project } = useRequireCurrentProject();

    const {
        data: { researchTypes, protocols },
        loading,
    } = useFeatureCreateDialogData({ open, projectId: project.id });

    const { draftSelectedOption, setDraftSelectedOption, isValid, validate } = useDraftSelectedOption({
        open,
        initialOption,
    });

    function handleCancel() {
        setDraftSelectedOption(initialOption);
        onClose();
    }

    function handleConfirm() {
        if (!validate(draftSelectedOption)) return;

        onConfirm(draftSelectedOption);
        onClose();
    }

    if (loading) return false;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{ transition: "max-height 1s ease-in-out" }}>
            <MapEditorFeatureCreateDialogTitle onClose={handleCancel} />

            <DialogContent sx={{ padding: 2 }}>
                <Stack rowGap={2}>
                    <DrawTypeSection
                        selectedDrawType={draftSelectedOption.type}
                        onDrawTypeSelect={newDrawType => {
                            setDraftSelectedOption({ type: newDrawType });
                        }}
                        allowedDrawTypes={allowedDrawTypes}
                    />
                    {draftSelectedOption.type === "area" && (
                        <PlannerAreaSection
                            draftSelectedOption={draftSelectedOption}
                            onUpdate={setDraftSelectedOption}
                            protocols={protocols}
                        />
                    )}
                    {draftSelectedOption.type === "other" && (
                        <OtherSection draftSelectedOption={draftSelectedOption} onUpdate={setDraftSelectedOption} />
                    )}
                    {draftSelectedOption.type === "observation" && (
                        <ObservationSection
                            draftSelectedOption={draftSelectedOption}
                            onUpdate={setDraftSelectedOption}
                            researchTypes={researchTypes}
                        />
                    )}
                    {draftSelectedOption.type === "ecologicalFunction" && (
                        <EcologicalFunctionSection
                            draftSelectedOption={draftSelectedOption}
                            onUpdate={setDraftSelectedOption}
                            researchTypes={researchTypes}
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ padding: 2, display: "flex", justifyContent: "space-between" }}>
                <Button onClick={handleCancel}>{t("mapEditorFeatureCreateDialog.btnCancel")}</Button>
                <Button variant="contained" onClick={handleConfirm} endIcon={<ChevronRight />} disabled={!isValid}>
                    {t("mapEditorFeatureCreateDialog.btnStartDrawing")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
